import { useTranslation } from 'react-i18next';
import { Column, ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import ArrowDownIconSolid from '@/components/icons/ArrowDownIconSolid';
import { useTenantsFilter } from '@/features/superAdmin/features/tenants/hooks/useTenantsFilter';
import { TenantListItemResponseDto } from '../types/superAdmin-tenants-dto';

const TenantsPageColumns = (): ColumnDef<TenantListItemResponseDto>[] => {
	const { t } = useTranslation();

	const { setFilter } = useTenantsFilter();

	const handleSort = (
		columnKey: Capitalize<keyof TenantListItemResponseDto>,
		column: Column<TenantListItemResponseDto>,
	) => {
		const newDirection = column.getIsSorted() === 'asc';
		column.toggleSorting(newDirection);

		setFilter({
			orderBy: columnKey,
			orderDirection: newDirection ? 'asc' : 'desc',
		});
	};

	return [
		{
			accessorKey: 'id',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('Id', column)}>
						{t('main.superAdmin.tenants.table.header.id')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={'text-sm text-popover-800'}>
					{row.getValue('id')}
				</div>
			),
		},
		{
			accessorKey: 'name',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('Name', column)}>
						{t('main.superAdmin.tenants.table.header.name')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={'flex items-center gap-2.5'}>
					<h4 className='text-sm text-popover-800 truncate'>
						{row.getValue('name')}
					</h4>
				</div>
			),
		},
		{
			accessorKey: 'enabled',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('Enabled', column)}>
						{t('main.superAdmin.tenants.table.header.status')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={'text-sm text-popover-800'}>
					{row.getValue('enabled') ? (
						<span className='text-success-1000'>
							{t(
								'main.superAdmin.tenants.table.statuses.enabled',
							)}
						</span>
					) : (
						<span className='text-error-1000'>
							{t(
								'main.superAdmin.tenants.table.statuses.disabled',
							)}
						</span>
					)}
				</div>
			),
		},
	];
};

export default TenantsPageColumns;
